import React from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import _ from 'underscore';

import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import CreateRunResultsFlow from 'bulk-import/results/CreateRunResultsFlow';
import DataConnector from 'connect/DataConnector';
import {isSchemaTag, Tag} from 'folder-items/FileConstants';
import {StudyDesignType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import bulkUpdateAutomationOutputFileBlobs from 'lab-automation/automation-files/bulkUpdateAutomationOutputFileBlobs';
import {Review} from 'notebook/reviews/ReviewCriteriaConstants';
import preferences from 'Preferences';
import {ProcedureWorksheetContext} from 'procedures/worksheets/ProcedureWorksheetContext';
import {BenchlingDispatch, Thunk} from 'redux-stores/Store';
import {ASSAY_TYPES, AssayRunSchema, AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {fetchAssayResultsByRun} from 'results/assays/AssayRecordActions';
import {AssayRun, AssayResult, AssayRecordOriginType} from 'results/assays/AssayRecordConstants';
import AssayStore from 'results/AssayStore';
import legalResultSchemasForRun from 'results/legalResultSchemasForRun';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import canSetValidation from 'results/validation/canSetValidation';
import SetValidationButton from 'results/validation/SetValidationButton';
import {validatableInputsForRun} from 'results/validation/ValidatableInput';
import UpdateTestOrdersOnPlatesFieldModal from 'sample-tests/ui/test-orders/modals/UpdateTestOrdersOnPlatesFieldModal';
import {tagHasValue, filterToSchemaTags} from 'schemas/tags/TagUtil';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import * as modals from 'util/Modals';
import {Optional} from 'util/Types';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {NLADVersionedAutomationFiles} from '../AttachmentConstants';

interface BackToInboxButtonProps {
  onClick: () => void;
}

/**
 * Button that returns a run back to the runs inbox.
 */
function BackToInboxButton(props: BackToInboxButtonProps): JSX.Element {
  return (
    <Tooltip title="Move back to inbox" key="back-to-inbox-button">
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className="u-mini-spacing-left"
        icon="move-to-inbox"
        size={ButtonSize.SMALL}
        onClick={props.onClick}
        dataTestAttrs={{component: 'BackToInboxButton', element: 'button'}}
      />
    </Tooltip>
  );
}

interface UploadResultsProps {
  assayRun: AssayRun;
  fetchAssayResultsByRun: (assayRunId: string) => Thunk<Promise<void>>;
  resultSchemasForRun: Array<AssaySchema>;
}

export function UploadResultsButton(props: UploadResultsProps): JSX.Element {
  const buttonProps: ButtonProps = {
    buttonStyle: ButtonStyle.TERTIARY_GRAY,
    icon: 'upload',
    size: ButtonSize.SMALL,
    className: 'u-mini-spacing-left',
  };

  if (props.resultSchemasForRun.length === 0) {
    return (
      <Tooltip title={`No ${MODEL_NAMES.result} schemas are configured to link to this run`}>
        <Button {...buttonProps} disabled={true} />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={`Upload ${MODEL_NAMES.results}`}>
      <Dropdown
        menu={
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.XLARGE} dropLeft={true} scrollable={true}>
            {props.resultSchemasForRun.map((resultSchema) => (
              <Option
                key={resultSchema.id}
                value={resultSchema.id}
                onSelect={() => {
                  CreateRunResultsFlow.open({
                    assayRunId: props.assayRun.id,
                    resultSchema,
                    onImportRows: () => {
                      props.fetchAssayResultsByRun(props.assayRun.id);
                    },
                  });
                }}
                shouldEllipsize={true}
              >
                {resultSchema.name}
              </Option>
            ))}
          </Menu>
        }
      >
        <Button {...buttonProps} />
      </Dropdown>
    </Tooltip>
  );
}

interface EditRunToolbarProps {
  assayResults: Array<AssayResult> | null;
  assayRun: AssayRun;
  assayRunSchema: AssayRunSchema;
  childrenFetching: boolean;
  fetchAssayResultsByRun: (assayRunId: string) => Thunk<Promise<void>>;
  handleRefreshVersionedData: () => Promise<void>;
  handleRemoveAssayRun: () => void;
  handleUpdateAutomationFiles: (files: Partial<NLADVersionedAutomationFiles>) => void;
  locked: boolean;
  disableEditButton: boolean;
  onClickEdit: () => void;
  review: Review | null;
  automationFiles: NLADVersionedAutomationFiles;
}

interface EnhancedProps {
  resultSchemasForRun: Optional<Array<AssaySchema>>;
}

class EditRunToolbarConnector extends DataConnector<EditRunToolbarProps, EnhancedProps> {
  override stores = [AssayStore];

  getResultSchemasForRun(): Optional<Array<AssaySchema>> {
    const resultSchemas = AssayStore.getAssaySchemas(ASSAY_TYPES.ASSAY_RESULT);
    if (!this.props.assayRun || !resultSchemas) {
      return null;
    } else {
      return legalResultSchemasForRun(resultSchemas, this.props.assayRun.schemaId);
    }
  }

  enhanceProps(): EnhancedProps {
    return {
      resultSchemasForRun: this.getResultSchemasForRun(),
    };
  }
}

function _getLinkedResultsValidationWarningMessage(assayResults: Array<AssayResult> | null): string | null {
  return assayResults && assayResults.length
    ? `Validation will be set on ${assayResults.length} ${pluralize(
        assayResults.length,
        MODEL_NAMES.result
      )} linked to this ${MODEL_NAMES.run}.`
    : null;
}

interface UploadOutputFileBlobsProps {
  automationOutputFiles: Array<AutomationOutputFileGraphQL>;
  runId: string;
  onUpdateOutputFiles: (updatedOutputFiles: Array<AutomationOutputFileGraphQL>) => void;
}

function UploadOutputFileBlobs(props: UploadOutputFileBlobsProps): JSX.Element {
  const availableOutputFiles = props.automationOutputFiles.filter((outputFile) => !outputFile.blob);
  const disabled = availableOutputFiles.length === 0;
  return (
    <Tooltip title={disabled ? 'All output files have been uploaded.' : 'Bulk upload output files'}>
      <Button
        icon="upload"
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className="u-mini-spacing-left"
        onClick={() => {
          bulkUpdateAutomationOutputFileBlobs(props.runId, availableOutputFiles, props.onUpdateOutputFiles);
        }}
        size={ButtonSize.SMALL}
        disabled={disabled}
      />
    </Tooltip>
  );
}

export function OverflowDropdown(props: {
  className?: string;
  allowArchive: boolean;
  allowCopyId: boolean;
  handleRemoveAssayRun: () => void;
  isAutomationRun: boolean;
  assayRun: AssayRun;
  assayRunSchema: AssayRunSchema;
}): JSX.Element {
  const {PMEIs, isProcedureFeatureEnabled} = ProcedureWorksheetContext.useContext();
  const shouldShowUpdatePlateTestOrdersOption =
    BenchlingConfig.SAMPLE_TESTS_ENABLE_MATRIX_PLATE_TEST_ORDER_AUTOMATION_WORKAROUND &&
    isProcedureFeatureEnabled &&
    PMEIs.length === 1 &&
    !isInaccessible(PMEIs[0]) &&
    !isInaccessible(PMEIs[0].procedureRun.study) &&
    PMEIs[0].procedureRun.study.studyDesign != null &&
    PMEIs[0].procedureRun.study.studyDesign.type === StudyDesignType.TRIAL_SAMPLE_TESTING;

  const _copyId = (): void => {
    copyToClipboard(props.assayRun.id, {showToastOnSuccess: true});
  };
  const _exportAuditLogs = (): void => {
    if (props.assayRun.apiId) {
      AuditActions.exportAuditLogs([props.assayRun.apiId]);
    }
  };
  return (
    <Dropdown
      menu={
        <Menu height={MenuHeight.MEDIUM} width={MenuWidth.MEDIUM} dropLeft={true}>
          <Section>
            {props.allowArchive && (
              <Option
                onSelect={async () => {
                  const confirm = await Promise.resolve(
                    modals.confirm('Archive', 'Are you sure you want to archive?', ButtonStyle.NEGATIVE, {
                      promiseCompatible: true,
                      enableRedesign: true,
                    })
                  );
                  if (confirm) {
                    props.handleRemoveAssayRun();
                  }
                }}
                dataTestAttrs={{
                  component: 'UnconnectedEditRunToolbar',
                  element: 'option',
                  key: 'archive',
                }}
              >
                {props.isAutomationRun ? 'Archive run' : 'Archive'}
              </Option>
            )}
          </Section>
          <Section>{props.allowCopyId && <Option onSelect={_copyId}>Copy API ID</Option>}</Section>
          <Section>
            {props.assayRun.apiId && (
              <Option
                onSelect={_exportAuditLogs}
                dataTestAttrs={{
                  component: 'UnconnectedEditRunToolbar',
                  element: 'option',
                  key: 'exportAuditLog',
                }}
              >
                {EXPORT_AUDIT_LOG_DISPLAY_STRING}
              </Option>
            )}
          </Section>
          {shouldShowUpdatePlateTestOrdersOption && (
            <Section>
              <Option
                onSelect={() => {
                  legacyRunModal(UpdateTestOrdersOnPlatesFieldModal, {
                    pmeiId: getAccessibleOrInaccessibleId(PMEIs[0]),
                    schemaFields: props.assayRunSchema.fields,
                    schemaTags: props.assayRun.tags.filter((v) => isSchemaTag(v)),
                    runName: props.assayRunSchema.name,
                  });
                }}
              >
                Update test orders
              </Option>
            </Section>
          )}
        </Menu>
      }
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className={props.className}
        icon="overflow"
        size={ButtonSize.SMALL}
        dataTestAttrs={{
          component: 'UnconnectedEditRunToolbar',
          element: 'overflowButton',
        }}
      />
    </Dropdown>
  );
}

/**
 * Toolbar for interacting with an assay run.
 */

export class UnconnectedEditRunToolbar extends React.Component<EditRunToolbarProps & EnhancedProps> {
  _handleRefreshVersionedData = async (): Promise<void> => {
    await this.props.handleRefreshVersionedData();
  };
  _dataLoaded = (): boolean =>
    this.props.assayResults != null && !this.props.childrenFetching && this.props.resultSchemasForRun != null;

  override render(): JSX.Element {
    const isAutomationRun =
      this.props.assayRunSchema.automationInputFileConfigs.length > 0 ||
      this.props.assayRunSchema.automationOutputFileConfigs.length > 0;

    const validationButton = this._dataLoaded() &&
      canSetValidation({
        assayRun: this.props.assayRun,
        assayRunSchema: this.props.assayRunSchema,
        attachedForReview: true,
        review: this.props.review,
      }) && (
        <SetValidationButton
          className="u-mini-spacing-left"
          modalWarningMessage={_getLinkedResultsValidationWarningMessage(this.props.assayResults)}
          size={ButtonSize.SMALL}
          validatableInputs={validatableInputsForRun(this.props.assayRun)}
        />
      );

    const uploadButton =
      this._dataLoaded() && !isAutomationRun ? (
        <UploadResultsButton
          assayRun={this.props.assayRun}
          resultSchemasForRun={this.props.resultSchemasForRun!}
          fetchAssayResultsByRun={this.props.fetchAssayResultsByRun}
        />
      ) : null;

    const uploadOutputFileBlobs =
      isAutomationRun &&
      BenchlingConfig.ENABLE_LAB_AUTOMATION_BULK_UPLOAD_OUTPUT_FILES &&
      this.props.automationFiles.automationOutputFiles ? (
        <UploadOutputFileBlobs
          runId={this.props.assayRun.id}
          automationOutputFiles={this.props.automationFiles.automationOutputFiles}
          onUpdateOutputFiles={(updatedOutputFiles) => {
            const outputFiles = this.props.automationFiles.automationOutputFiles;
            const updatedOutputFileByName = _.indexBy(
              updatedOutputFiles,
              (f) => f.automationOutputFileConfig.name
            );
            this.props.handleUpdateAutomationFiles({
              automationOutputFiles: outputFiles?.map(
                (outputFile) =>
                  updatedOutputFileByName[outputFile.automationOutputFileConfig.name] || outputFile
              ),
            });
          }}
        />
      ) : null;

    const refreshButton = isAutomationRun ? (
      <Tooltip title="Sync run data">
        <span>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="u-mini-spacing-left"
            icon="assembly"
            onClick={this._handleRefreshVersionedData}
            size={ButtonSize.SMALL}
          />
        </span>
      </Tooltip>
    ) : null;

    if (this.props.locked) {
      return (
        <span>
          {validationButton}
          {this.props.assayRun.apiId && (
            <OverflowDropdown
              className="u-mini-spacing-left"
              allowArchive={false}
              allowCopyId={false}
              assayRun={this.props.assayRun}
              assayRunSchema={this.props.assayRunSchema}
              handleRemoveAssayRun={() => {}}
              isAutomationRun={isAutomationRun}
            />
          )}
        </span>
      );
    }

    const allowArchive =
      this.props.assayRun.originType === AssayRecordOriginType.ELN &&
      !BenchlingConfig.ENABLE_LAB_AUTOMATION_BULK_UPLOAD_OUTPUT_FILES;
    const allowCopyId = Boolean(preferences.get('ENABLE_COPY_API_ID'));
    const showOverflow =
      allowArchive ||
      allowCopyId ||
      BenchlingConfig.SAMPLE_TESTS_ENABLE_MATRIX_PLATE_TEST_ORDER_AUTOMATION_WORKAROUND;

    return (
      <span>
        {refreshButton}
        {uploadOutputFileBlobs}
        {uploadButton}
        {validationButton}
        {this.props.assayRun.originType === AssayRecordOriginType.API && (
          <BackToInboxButton onClick={this.props.handleRemoveAssayRun} />
        )}
        {showOverflow && (
          <OverflowDropdown
            className="u-mini-spacing-left"
            allowArchive={allowArchive}
            allowCopyId={allowCopyId}
            assayRun={this.props.assayRun}
            assayRunSchema={this.props.assayRunSchema}
            handleRemoveAssayRun={this.props.handleRemoveAssayRun}
            isAutomationRun={isAutomationRun}
          />
        )}
        {(BenchlingConfig.LAB_AUTOMATION_ALLOW_EDITING_API_RUNS ||
          this.props.assayRun.originType === AssayRecordOriginType.ELN) && (
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            className="u-mini-spacing-left"
            disabled={this.props.disableEditButton}
            onClick={this.props.onClickEdit}
            size={ButtonSize.SMALL}
            dataTestAttrs={{component: 'UnconnectedEditRunToolbar', element: 'editButton'}}
            text="Edit"
          />
        )}
      </span>
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: BenchlingDispatch) =>
  bindActionCreators({fetchAssayResultsByRun}, dispatch);

export const EditRunToolbar = connect(
  null,
  mapDispatchToProps
)(EditRunToolbarConnector.connect(UnconnectedEditRunToolbar));

export function validateRunForSubmit(
  tags: Optional<Array<Tag>>,
  connectionId: string | null,
  connectionSchema: {id: string} | null
): string | null {
  if (tags != null) {
    const requiredTags = filterToSchemaTags(tags).filter((schemaTag) => schemaTag.tagSchemaField.isRequired);
    for (const tag of requiredTags) {
      if (!tagHasValue(tag)) {
        return `${tag.name} is required`;
      }
    }
  }
  if (connectionSchema && !connectionId) {
    return 'Connection is required';
  }
  return null;
}

interface SubmitButtonProps {
  onClick: () => void;
  error: string | null;
  text: string;
  dataTestAttrs?: DataTestAttrs;
}

export function SubmitButton(props: SubmitButtonProps): JSX.Element {
  return (
    <Tooltip title={props.error} disabled={props.error == null}>
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        className="u-mini-spacing-left"
        size={ButtonSize.SMALL}
        onClick={props.onClick}
        text={props.text}
        disabled={props.error != null}
        dataTestAttrs={props.dataTestAttrs}
      />
    </Tooltip>
  );
}

interface AddRunToolbarProps {
  runSchema: AssayRunSchema;
  onClickCreate: () => void;
  onClickInsertFromInbox: () => void;
  validateForSubmit: () => string | null;
}

/**
 * Generic toolbar for submitting an action.
 */
export function AddRunToolbar(props: AddRunToolbarProps): JSX.Element {
  const error = props.validateForSubmit();
  return (
    <span>
      {props.runSchema.allowInsertRunFromInbox ? (
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          icon="insert-from-inbox"
          onClick={props.onClickInsertFromInbox}
          text="Insert from inbox"
          dataTestAttrs={{component: 'AddRunToolbar', element: 'insertFromInboxButton'}}
        />
      ) : null}
      {props.runSchema.allowCreateRunFromNlad ? (
        <SubmitButton
          onClick={props.onClickCreate}
          text="Create"
          error={error}
          dataTestAttrs={{component: 'AddRunToolbar', element: 'submitButton'}}
        />
      ) : null}
    </span>
  );
}

interface SubmitToolbarProps {
  onCancel: () => void;
  onSubmit: () => void;
  validateForSubmit: () => string | null;
  submitText: string;
}

/**
 * Generic toolbar for submitting an action.
 */
export function SubmitToolbar(props: SubmitToolbarProps): JSX.Element {
  const error = props.validateForSubmit();
  return (
    <span>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        onClick={props.onCancel}
        text="Cancel"
      />
      <SubmitButton
        onClick={props.onSubmit}
        text={props.submitText}
        error={error}
        dataTestAttrs={{component: 'SubmitToolbar', element: 'saveButton'}}
      />
    </span>
  );
}
